import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { srcSetFor } from "@/lib/responsive";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Our Work", path: "/our-work" },
  { label: "About", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

const StickyHomeNav = () => {
  const [visible, setVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.header
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-50 bg-card shadow-md border-b border-border"
        >
          <div className="container mx-auto flex items-center justify-between py-2 px-4 lg:px-8">
            <Link to="/" className="flex-shrink-0">
              <img src={logo} srcSet={srcSetFor(logo)} sizes="300px" alt="JTB Floors" width={1536} height={1024} decoding="async" className="h-10 w-auto" />
            </Link>
            <nav className="hidden md:flex items-center gap-1" aria-label="Sticky navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-primary rounded-md transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-3">
              <Button variant="outline" size="sm" asChild className="gap-2">
                <Link to="/contact" hash="form">Request Estimate</Link>
              </Button>
              <Button asChild size="sm" className="btn-shine gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </Button>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <Button asChild size="sm" className="btn-shine gap-1.5 text-sm px-3 py-1.5">
                <a href="tel:17739342004">
                  <Phone className="w-4 h-4" /> Call
                </a>
              </Button>
              <button
                className="p-2 rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {mobileOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden overflow-hidden bg-card border-b border-border"
              >
                <nav className="flex flex-col px-4 py-4 gap-1" aria-label="Mobile sticky navigation">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 rounded-md text-base font-medium text-foreground/80 hover:bg-muted transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="flex flex-col gap-2 mt-4">
                    <Button variant="outline" asChild className="gap-2 justify-center">
                      <Link to="/contact" hash="form" onClick={() => setMobileOpen(false)}>Request Estimate</Link>
                    </Button>
                    <Button asChild className="btn-shine justify-center gap-2">
                      <a href="tel:17739342004">
                        <Phone className="w-4 h-4" /> Call (773) 934-2004
                      </a>
                    </Button>
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>
      )}
    </AnimatePresence>
  );
};

export default StickyHomeNav;
